import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { MARCA_EJEMPLO } from "@/modules/analisis_financiero/leyendas";
import {
  eliminarReferencia,
  guardarReferencia,
  obtenerAuditoriaReferencias,
  obtenerReferencias,
} from "@/modules/analisis_financiero/analisisFinancieroApi";
import { pctMx } from "@/modules/analisis_financiero/semaforoUi";

const TIPOS = [
  { id: "ejemplo", label: "EJEMPLO" },
  { id: "despacho", label: "Criterio del despacho" },
  { id: "inegi", label: "INEGI" },
  { id: "ley_contexto", label: "Contexto de ley (no alimenta el semáforo)" },
];

const VACIO = {
  giro_scian: "",
  giro_descripcion: "",
  rubro: "sueldos",
  minimo_pct: "",
  maximo_pct: "",
  muy_alto_pct: "",
  fuente: "",
  url: "",
  anio_dato: "",
  nota: "",
  tipo_fuente: "ejemplo",
};

function numOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export default function ReferenciasTable({ rubros, puedeEscribir }) {
  const [filas, setFilas] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [mostrarBitacora, setMostrarBitacora] = useState(false);
  const [edit, setEdit] = useState(null);
  const [cargando, setCargando] = useState(true);

  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await obtenerReferencias();
      setFilas(Array.isArray(data?.renglones) ? data.renglones : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo cargar la tabla de referencias.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    recargar();
  }, [recargar]);

  const abrir = (fila) => {
    if (!puedeEscribir) return;
    setEdit({
      id: fila?.id || "",
      origen: fila?.origen_fila || "",
      giro_scian: fila?.giro_scian || "",
      giro_descripcion: fila?.giro_descripcion || "",
      rubro: fila?.rubro || "sueldos",
      minimo_pct: fila?.minimo_pct ?? "",
      maximo_pct: fila?.maximo_pct ?? "",
      muy_alto_pct: fila?.muy_alto_pct ?? "",
      fuente: fila?.fuente || "",
      url: fila?.url || "",
      anio_dato: fila?.anio_dato ?? "",
      nota: fila?.nota || "",
      tipo_fuente: fila?.tipo_fuente || "ejemplo",
    });
  };

  const guardar = async () => {
    if (!edit) return;
    const payload = {
      giro_scian: String(edit.giro_scian || "").trim(),
      giro_descripcion: edit.giro_descripcion,
      rubro: edit.rubro,
      minimo_pct: numOrNull(edit.minimo_pct),
      maximo_pct: Number(edit.maximo_pct),
      muy_alto_pct: numOrNull(edit.muy_alto_pct),
      fuente: edit.fuente,
      url: edit.url,
      anio_dato: numOrNull(edit.anio_dato),
      nota: edit.nota,
      tipo_fuente: edit.tipo_fuente,
    };
    try {
      await guardarReferencia(payload, edit.id || undefined);
      toast.success("Referencia guardada. El cambio quedó en la bitácora.");
      setEdit(null);
      await recargar();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo guardar la referencia.");
    }
  };

  const borrar = async (fila) => {
    if (!puedeEscribir || fila.origen_fila !== "despacho") return;
    try {
      await eliminarReferencia(fila.id);
      toast.success("Se quitó la copia del despacho. Si había semilla, vuelve a mostrarse.");
      await recargar();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo borrar la referencia.");
    }
  };

  const verBitacora = async () => {
    setMostrarBitacora(true);
    try {
      const { data } = await obtenerAuditoriaReferencias();
      setEventos(Array.isArray(data?.eventos) ? data.eventos : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo leer la bitácora.");
    }
  };

  return (
    <section className="space-y-3" data-testid="referencias-tabla">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-[#0F2D5C]">Tabla de referencias por giro</h3>
          <p className="text-xs text-amber-900">
            {MARCA_EJEMPLO}. Los valores iniciales son editables y no son un criterio de la autoridad.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-sm border border-[#0F2D5C]/30 px-3 py-1.5 text-xs font-semibold text-[#0F2D5C]"
            onClick={verBitacora}
            data-testid="referencias-bitacora"
          >
            Ver bitácora
          </button>
          {puedeEscribir ? (
            <button
              type="button"
              className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white"
              onClick={() => abrir({ ...VACIO, id: "" })}
              data-testid="referencias-nueva"
            >
              Nueva fila
            </button>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-slate-200">
        <table className="min-w-full text-left text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2">Giro</th>
              <th className="px-2 py-2">Rubro</th>
              <th className="px-2 py-2">Mínimo</th>
              <th className="px-2 py-2">Máximo</th>
              <th className="px-2 py-2">Muy alto</th>
              <th className="px-2 py-2">Fuente</th>
              <th className="px-2 py-2">Año</th>
              <th className="px-2 py-2">Nota</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td className="px-2 py-3" colSpan={9}>Cargando referencias…</td></tr>
            ) : null}
            {!cargando && filas.length === 0 ? (
              <tr><td className="px-2 py-3" colSpan={9}>No hay filas.</td></tr>
            ) : null}
            {filas.map((fila) => (
              <tr key={`${fila.id}-${fila.giro_scian}-${fila.rubro}`} className="border-t border-slate-100" data-testid={`ref-${fila.giro_scian}-${fila.rubro}`}>
                <td className="px-2 py-2">
                  <div className="font-medium">{fila.giro_scian}</div>
                  <div className="text-slate-500">{fila.giro_descripcion}</div>
                </td>
                <td className="px-2 py-2">{fila.rubro}</td>
                <td className="px-2 py-2">{pctMx(fila.minimo_pct)}</td>
                <td className="px-2 py-2">{pctMx(fila.maximo_pct)}</td>
                <td className="px-2 py-2">{pctMx(fila.muy_alto_pct)}</td>
                <td className="px-2 py-2 max-w-[220px]">
                  {fila.es_ejemplo ? (
                    <span className="mr-1 rounded-sm bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-950">EJEMPLO</span>
                  ) : null}
                  {fila.url ? (
                    <a href={fila.url} target="_blank" rel="noreferrer" className="underline text-[#0F2D5C]">{fila.fuente || fila.url}</a>
                  ) : (
                    <span>{fila.fuente}</span>
                  )}
                </td>
                <td className="px-2 py-2">{fila.anio_dato || "—"}</td>
                <td className="px-2 py-2 max-w-[200px] text-slate-600">{fila.nota}</td>
                <td className="px-2 py-2 whitespace-nowrap">
                  {puedeEscribir ? (
                    <>
                      <button type="button" className="mr-2 underline" onClick={() => abrir(fila)}>Editar</button>
                      {fila.origen_fila === "despacho" ? (
                        <button type="button" className="underline text-red-800" onClick={() => borrar(fila)}>Borrar copia</button>
                      ) : null}
                    </>
                  ) : (
                    <span className="text-slate-400">Solo lectura</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit ? (
        <div className="rounded-sm border border-[#0F2D5C]/20 bg-slate-50 p-3 space-y-2" data-testid="referencias-editor">
          <p className="text-xs font-semibold text-[#0F2D5C]">
            {edit.id ? "Editar referencia" : "Nueva referencia"}
          </p>
          <div className="grid gap-2 md:grid-cols-3">
            <label className="text-xs">Clave SCIAN
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.giro_scian} onChange={(e) => setEdit({ ...edit, giro_scian: e.target.value })} />
            </label>
            <label className="text-xs md:col-span-2">Descripción del giro
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.giro_descripcion} onChange={(e) => setEdit({ ...edit, giro_descripcion: e.target.value })} />
            </label>
            <label className="text-xs">Rubro
              <select className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.rubro} onChange={(e) => setEdit({ ...edit, rubro: e.target.value })}>
                {(rubros || []).map((r) => <option key={r.id} value={r.id}>{r.etiqueta}</option>)}
              </select>
            </label>
            <label className="text-xs">Mínimo %
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.minimo_pct} onChange={(e) => setEdit({ ...edit, minimo_pct: e.target.value })} />
            </label>
            <label className="text-xs">Máximo %
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.maximo_pct} onChange={(e) => setEdit({ ...edit, maximo_pct: e.target.value })} />
            </label>
            <label className="text-xs">Muy alto %
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.muy_alto_pct} onChange={(e) => setEdit({ ...edit, muy_alto_pct: e.target.value })} />
            </label>
            <label className="text-xs">Año del dato
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.anio_dato} onChange={(e) => setEdit({ ...edit, anio_dato: e.target.value })} />
            </label>
            <label className="text-xs">Tipo
              <select className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.tipo_fuente} onChange={(e) => setEdit({ ...edit, tipo_fuente: e.target.value })}>
                {TIPOS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </label>
            <label className="text-xs md:col-span-3">Fuente
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.fuente} onChange={(e) => setEdit({ ...edit, fuente: e.target.value })} />
            </label>
            <label className="text-xs md:col-span-3">URL
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.url} onChange={(e) => setEdit({ ...edit, url: e.target.value })} />
            </label>
            <label className="text-xs md:col-span-3">Nota
              <input className="mt-1 w-full rounded-sm border px-2 py-1" value={edit.nota} onChange={(e) => setEdit({ ...edit, nota: e.target.value })} />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="button" className="rounded-sm bg-[#0F2D5C] px-3 py-1.5 text-xs font-semibold text-white" onClick={guardar} data-testid="referencias-guardar">
              Guardar referencia
            </button>
            <button type="button" className="rounded-sm border px-3 py-1.5 text-xs" onClick={() => setEdit(null)}>Cancelar</button>
          </div>
        </div>
      ) : null}

      {mostrarBitacora ? (
        <div className="rounded-sm border border-slate-200 p-3" data-testid="referencias-bitacora-lista">
          <h4 className="text-xs font-semibold mb-2">Bitácora de cambios</h4>
          {eventos.length === 0 ? <p className="text-xs text-slate-500">Todavía no hay cambios del despacho.</p> : null}
          <ul className="space-y-1 text-xs">
            {eventos.map((ev) => (
              <li key={ev.id}>
                <span className="font-medium">{ev.accion}</span>
                {" · "}
                {ev.created_at}
                {ev.despues?.giro_scian ? ` · ${ev.despues.giro_scian} / ${ev.despues.rubro}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
